/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 08-09-2026
 *
 * De samenwerk-baas: de uitvoering van het samenwerkprotocol. Hij bezit het verkeer (de twee
 * verzendkanalen en de binnenkomende notificaties), de registratie van dit model bij het backend,
 * en straks de dispatcher met de actietabel.
 *
 * Bewust een aparte service naast `SamenwerkingService` (kernbesluit 8): die tweede is
 * sessie-*state* met veel componentlezers, dit is protocol-*uitvoering* met één registratiepunt.
 * Samenvoegen zou de state-service laten uitgroeien tot het hart van het protocol.
 *
 * Afhankelijkheidsrichting: de baas staat bovenaan. Hij injecteert de opslagketen, het document, de
 * norm en de sessie-state; hém injecteren alleen componenten, de flowservices (`ModelbeheerService`,
 * `SimulatieService`, `ReplayService`), de `CanvasregistratieService` en de testingang. Wat andere
 * diensten hem te vertellen hebben komt binnen als melding (`bijUndostatus`, `bijNormdata`), niet als
 * terugbelverzoek — anders ontstaat de cyclus die kernbesluit 6 vermijdt.
 */
import {inject, Injectable} from '@angular/core';
import {Sihwlog} from 'sihw-ngx';
import type {NodeSingular} from 'cytoscape';
import {ApiService} from '../backend/api.service';
import {DlModel} from '../model/dl-model';
import {OpslagService, type Tekenaanleiding} from '../model/opslag.service';
import {herkomstVanAanleiding} from '../../lib/feedbackherkomst';
import type {Feedbackherkomst} from '../../lib/feedbackherkomst';
import {NormService} from '../engines/norm.service';
import {BouwsuggestieEngineService} from '../engines/bouwsuggestie-engine.service';
import {CondtagService} from '../../services/condtag.service';
import {FeedbackbalkService} from '../../services/feedbackbalk.service';
import type {FeedbackToggleSoort} from '../../services/feedbackbalk.service';
import {FeedbackSelectieService} from '../../services/feedback-selectie.service';
import type {SimSelectie} from '../../services/feedback-selectie.service';
import {ProjectVlaggenService} from '../../services/project-vlaggen.service';
import {SimulatieStatusService} from '../../services/simulatie-status.service';
import {SamenwerkingService} from '../../services/samenwerking.service';
import type {SamenwerkAanwezige, Werkmodus} from '../../services/samenwerking.service';
import {SessieService} from '../../services/sessie.service';
import {ToastService} from '../../services/toast.service';
import {SchermindelingService} from '../../services/schermindeling.service';
import {Melding} from '../../lib/melding';
import {Modelacties} from './modelacties';
import type {Actieuitkomst} from './modelacties';
import {maakActietabel} from './actietabel';
import type {Actieargumenten, Actieregel, Berichtcontext} from './actietabel';
import {maakSimulatietabel} from './simulatietabel';
import {SimfeedbackEngineService} from '../engines/simfeedback-engine.service';
import type {SimfeedbackData} from '../engines/simfeedback-engine.service';
import type {ModelId} from '../backend/api.service';
import type {CondtagState} from '../../services/condtag.service';
import type {ModelchangedNotify, Samenwerkactie, Samenwerkbericht, Simulatie_eiland, Simulatiebericht} from './protocol.types';
import type {Propswijziging} from '../model/opslag.service';
import type {IneqHistoryEntry, SimulatieData} from '../../components/canvas/dl-simcanvas/dl-simcanvas.component';

/**
 * De simfeedback zoals hij over de simulatielijn gaat en in een snapshot terechtkomt: of hij
 * getoond wordt, wat de engine uitrekende en wat er aangewezen staat. De veldnamen zijn het
 * bestaande wire-contract.
 */
export interface SimfeedbackStand {
    showSimfeedback: boolean;
    feedback: SimfeedbackData | null;
    selectedsimfeedback: SimSelectie | null;
}

/**
 * De wire-/snapshotvorm van de simulatiestatus: het `simstatus`-simulatiebericht en het
 * `simstatus`-veld van de actielog-snapshots. De veldnamen zijn het bestaande wire-contract (uit de
 * AngularJS-tijd) en staan ook in opgeslagen snapshots; de echte state leeft verdeeld over het
 * simcanvas (de simulatie, de canvasstand en de selectievolgorde), de `SimulatieStatusService` (de
 * history-vlaggen), `DlModel` (de value-history-scratch per quantity) en de `SchermindelingService`
 * (de splitter-richting). Sinds golf a5 bezit de baas de twee adapters
 * (`haalSimulatiestatus`/`pasSimulatiestatusToe`); ze horen bij de `SimulatieService`, maar die
 * verhuizing is in golf 3.6 niet meegegaan en staat nog open.
 */
export interface SimulatieStatus {
    /** De stand van de selectievolgorde-teller; altijd meegestuurd. */
    selectievolgorde: number;
    /** Of er een simulatie geladen is. Bij `false` is de rest afwezig. */
    simuleren: boolean;
    /**
     * De volledige simulatiedata zoals het backend hem gaf (in snapshots van vóór 29-09-2026 inclusief
     * de ineq-history-state). Sinds 17-09-2026 niet meer in berichten en nieuwe snapshots: daar staat
     * alleen `invoerhash`, en wie toepast haalt op. Wel nog in snapshots van daarvoor, en in een stand
     * die een client zelf bewaart.
     */
    simulatie?: unknown;
    /** De geserialiseerde inhoud van het simcanvas (`cy.json().elements`). */
    elements?: unknown;
    /** Welke histories aanstaan. */
    history?: {value: boolean; ineq: boolean};
    /** Per quantity of de value-history getoond wordt en waar het blok staat. */
    valuehistories?: Record<string, {show: boolean; lefttop: {left: number; top: number} | undefined}>;
    /**
     * Per model-(on)gelijkheid of de ineq-history getoond wordt en waar het blok staat: een kopie van
     * `simulatie.ineqHistory`. Sinds 29-09-2026 een eigen veld, net als `valuehistories`: de map hing
     * aan de simulatie, en die reist sinds 17-09-2026 niet meer mee (bevinding testronde 4f, test 4).
     */
    ineqhistories?: Record<string, IneqHistoryEntry>;
    /** De splitter-richting van de afzender; de ontvanger doet er (bewust) niets mee. */
    resizeDirection?: 'hori' | 'verti';
    /** De simfeedback-status, die in dezelfde vorm meelift (kernbesluit 7: de wire blijft heel). */
    simfeedback?: SimfeedbackStand;
    /** Vraagt de ontvanger de simview opnieuw te fitten. */
    fit?: boolean;
    /**
     * De invoerhash van deze simulatie (golf 3.6). Reist mee zodat een replay bij het terugkeren kan
     * vaststellen of de bewaarde simulatie nog bij het model hoort — op de invoer zelf, niet op de
     * modelversie, die ook oploopt als er alleen iets verschoven is.
     */
    invoerhash?: string | null;
}

/**
 * Wat een bouwsuggestie meldt als de gebruiker er iets mee doet: het gebaar (`type`), de node waar
 * het bij hoort, of het model erdoor veranderde en of er iets te zien is dat een snapshot waard is.
 */
export interface Bouwsuggestieactie {
    type: string;
    node?: NodeSingular | null;
    wijziging?: boolean;
    visuelewijziging?: boolean;
    args?: Record<string, unknown>;
}

/**
 * Wat de baas van de simulatiekant nodig heeft: de haken van de `SimulatieService`, die zich ermee
 * aanmeldt (`registreerSimulatieAansturing`). De simfeedback loopt over de simulatielijn en niet over
 * de samenwerklijn: een volger vráágt zijn toggle en zijn selectie bij de leider, die past ze toe en
 * synct de hele simulatiestatus terug.
 */
export interface SimulatieAansturing {
    /**
     * Start de simulatie of stop hem, met het bijbehorende log- en snapshotmoment. Sinds golf 4e
     * registreert de `SimulatieService` deze haken zelf; een injectie kan niet, want deze service is
     * de kant die ze aanroept.
     */
    startOfStop: (volger?: string) => void;
    /** Zet een stap, of start de simulatie als hij nog dicht is. */
    volgendeStap: (volger?: string) => void;
    /** Wissel het snelste pad om en herstart de simulatie. */
    wisselSnelstePad: () => void;
    /** Leg de toestanden mooi neer. */
    mooieStates: (volger?: string) => void;
    /** Neem over dat de uitvoerder op een simulatie wacht (of niet meer); alleen bij een volger. */
    bezigVanLeider: (bezig: boolean, gestopt: boolean) => void;
}

/**
 * De notify die het backend stuurt als de samenstelling van de groep verandert.
 */
interface ClientnamesNotify {
    model: ModelId;
    names: SamenwerkAanwezige[];
}

/**
 * De notify die een nieuwe werkmodus meldt.
 */
interface WerkmodusNotify {
    model: ModelId;
    werkmodus: Werkmodus;
    /** Het hoogste volgnummer dat er voor dit model ligt; zie `OpslagService.zetSnapshotteller`. */
    snapshotvolgorde?: number;
    /** Hoe lang geleden de laatste snapshot van dit model geschreven is, in ms; `null` als er geen is. */
    snapshotleeftijd?: number | null;
}

@Injectable({providedIn: 'root'})
export class SamenwerkprotocolService {

    private readonly log = new Sihwlog('info', 'Samenwerkprotocol');

    /**
     * Het transport: de verzendkanalen en de notificatiestroom.
     */
    private readonly api = inject(ApiService);

    /**
     * De sessie-state van de samenwerking: rol, werkmodus, aanwezigen.
     */
    private readonly samenwerking = inject(SamenwerkingService);

    /**
     * De opslagketen: de modelidentiteit, de versie, de changeId-administratie en de undostand.
     */
    private readonly opslag = inject(OpslagService);

    /**
     * Het document; hier voor de readonly-stand en de replayvlag.
     */
    private readonly dlModel = inject(DlModel);

    /**
     * De norm: een leider stuurt zijn interpretaties naar de volgers.
     */
    private readonly normService = inject(NormService);

    /**
     * De sessie: de eigen weergavenaam, om onszelf uit de aanwezigenlijst te filteren.
     */
    private readonly sessie = inject(SessieService);

    /**
     * De conditieset waarin gewerkt wordt. Een binnengekomen stap kan er een meedragen; die wint,
     * zodat een uit de pas gelopen weergave een element niet in de verkeerde set laat landen.
     */
    private readonly condtagService = inject(CondtagService);

    /**
     * De bouwsuggesties: een leider draait de engine en stuurt zijn uitkomsten rond.
     */
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);

    /**
     * De projectvlaggen achter de bouwsuggestie-gating.
     */
    private readonly projectVlaggen = inject(ProjectVlaggenService);

    /**
     * Of de simulatie open staat; dan draaien de bouwsuggesties niet.
     */
    private readonly simulatieStatus = inject(SimulatieStatusService);

    /**
     * De feedbackbalk: zijn vier toggles zijn gedeelde documentstate.
     */
    private readonly feedbackbalk = inject(FeedbackbalkService);

    /**
     * De feedbackselectie: wie een fout of een suggestie aanwijst, wijst hem voor iedereen aan.
     */
    private readonly feedbackSelectie = inject(FeedbackSelectieService);

    /**
     * De simfeedback-engine: zijn uitslag reist met de simulatiestand mee naar de volgers.
     */
    private readonly simfeedbackEngine = inject(SimfeedbackEngineService);

    /**
     * De meldingen: een geweigerd verzoek komt als toast bij de vrager terug.
     */
    private readonly toast = inject(ToastService);

    /**
     * De verdeling van het bouwscherm: de splitter-richting gaat mee in de simulatiestatus. Tot golf 4f
     * kreeg de baas hem via de migratie-interface aangereikt; de schermindeling injecteert zelf niets,
     * dus rechtstreeks geeft geen cyclus.
     */
    private readonly schermindeling = inject(SchermindelingService);

    /**
     * De uitvoerders van de modelacties: de graafmutaties met hun logregels en berichten.
     */
    private readonly modelacties = new Modelacties(this.dlModel, this.opslag, this.condtagService,
        this.samenwerking, this.log);

    /**
     * De actietabel: per actie wat de drie rollen ermee doen.
     */
    private readonly tabel = maakActietabel({
        acties: this.modelacties,
        dlModel: this.dlModel,
        opslag: this.opslag,
        normService: this.normService,
        feedbackbalk: this.feedbackbalk,
        feedbackSelectie: this.feedbackSelectie,
        bouwsuggestieEngine: this.bouwsuggestieEngine,
        toast: this.toast,
        syncNaarVolgers: () => this.syncNaarVolgers(),
        log: this.log
    });

    /**
     * Er stond nog niets op het canvas en na dit bericht wel. Een melding en geen aanroep: de baas
     * kan het modelcanvas niet injecteren, en een tweede canvasregistratie naast die van de tekenaar
     * is tegen de werkregel van 21-08-2026.
     */
    private readonly nasleepMelding = new Melding<void>();

    /**
     * De laatst gestarte opslagronde zolang die loopt; `wachtOpOpslagronde` wacht erop.
     */
    private lopendeOpslagronde: Promise<unknown> | null = null;

    /**
     * De simulatiekant: de haken van de `SimulatieService`; `null` tot die zich aangemeld heeft.
     */
    private simulatie: SimulatieAansturing | null = null;

    /**
     * Het simcanvas-eiland; `null` zolang er geen simulatiescherm is.
     */
    private simcanvas: Simulatie_eiland | null = null;

    /**
     * De laatst opgehaalde simulatie met zijn invoerhash. Opeenvolgende replaystappen en statusberichten
     * dragen meestal dezelfde hash; dan halen we hem niet nog eens op.
     */
    private opgehaaldeSimulatie: {hash: string; simulatie: unknown} | null = null;

    /**
     * Telt de toepassingen van een simulatiestatus. Het ophalen kan even duren, en komt er intussen een
     * nieuwere status (of een opruimactie), dan hoort de oudere niet meer getekend te worden.
     */
    private statusteller = 0;

    /**
     * De tweede baan van het protocol: de simulatie.
     */
    private readonly simtabel = maakSimulatietabel({
        eiland: () => this.simcanvas,
        aansturing: () => this.simulatie,
        //een `simstatus` van de leider komt hier binnen; het uitpakken raakt vier eigenaren plus het
        //simcanvas-eiland en zit sinds golf a5 bij de baas (`pasSimulatiestatusToe`)
        neemStatusOver: status => void this.pasSimulatiestatusToe(status as SimulatieStatus),
        neemSimfeedbackOver: status => this.pasSimfeedbackStatusToe(status as SimfeedbackStand | null),
        voerSimfeedbackactieUit: (actie, arg, volger) => this.voerSimfeedbackactieUit(actie, arg, volger),
        neemSnelstePadOver: waarde => {
            this.dlModel.fastest_path = waarde; //puur weergave; de leider heeft het al doorgerekend
        },
        //de uitvoerder wacht op een simulatie. Gaat langs de aansturing, want de SimulatieService kan
        //hier niet geïnjecteerd worden: die heeft deze baas zelf nodig
        neemBezigOver: (bezig, gestopt) => this.simulatie?.bezigVanLeider(bezig, gestopt)
    });

    constructor() {
        this.luisterNaarNotificaties();
        this.luisterNaarDeOpslag();
        this.luisterNaarDeNorm();
        this.luisterNaarDeFeedback();
        this.luisterNaarHetCondtagpaneel();
        this.luisterNaarHetSimcanvas();
        //de simulatiestand die in een snapshot meegaat komt uit drie eigenaren (het simcanvas, de
        //simulatiestatus en de simfeedback-engine); de opslagketen kent die niet en krijgt hem via
        //een registratie. Sinds golf a5 bij de baas — die heeft het simcanvas, de SimulatieStatus en
        //DlModel al — in plaats van bij de migratie-interface. Verhuist naar de SimulatieService met
        //de simulatiebaan.
        //`haalSimulatiestatus` zet de simfeedback-stand er zelf al in; hem daarnaast nog eens op het
        //hoogste niveau meegeven leverde hem dubbel in elke snapshot op (opgeruimd 15-09-2026). De
        //replay leest hem uit `simstatus`; oude snapshots houden het tweede exemplaar, dat niemand leest
        this.opslag.registreerSnapshotbron(() => {
            const simstatus = this.haalSimulatiestatus();
            return {simstatus, simhash: simstatus.simuleren ? simstatus.invoerhash ?? null : null};
        });
        //de laadketen meldt zijn modellen bij het backend aan en af; dat is protocol, dus van ons.
        //Bewust deze bestaande ingang en geen abonnement op `bijGeladen`/`bijGereset`: die vuren op
        //andere momenten (`bijGereset` bij élke load), en de laadketen awaitt deze haak omdat de
        //werkmodus moet staan voordat de norm de stand verwerkt.
        this.opslag.registreerSamenwerkhaak(deregistreer => this.registreerSamenwerken(deregistreer));
        //na elk vers document (`DlModel.tekenGraaf`): de simfeedback hoort bij het vorige en gaat weg, en
        //er volgt een verse bouwsuggestie-run. Het canvas laat zijn animatielock pas los als die promise
        //klaar is. Tot golf 4f registreerde de migratie-interface dit (plan-golf-4f.md, B5)
        this.dlModel.bijGraafGetekend(aanleiding => this.naGraafGetekend(aanleiding));
    }

    /* ******** De meldingen van de baas ******** */

    /**
     * Abonneert op het moment dat een binnengekomen bericht de eerste elementen op een leeg canvas
     * zet: dan hoort de weergave één keer op maat gebracht te worden. Bewust alleen bij ontvangen
     * berichten en niet bij een eigen eerste element (bestaand gedrag).
     */
    public bijEersteElementen(luisteraar: () => void): void {
        this.nasleepMelding.abonneer(luisteraar);
    }

    /**
     * Wacht tot de opslagronde van de laatste actie klaar is, met een limiet. Nodig voor de start van
     * een replay: zo'n ronde wacht zelf eerst de gedebouncete bouwsuggestie-run af, en zou daarna op
     * de dan al staande blokkade afketsen. De laatste actie zou dan nooit opgeslagen worden en de
     * replay zijn laatste stap missen. Geeft `false` als de limiet verliep.
     * @param limietMs hoe lang we hier hoogstens op wachten
     */
    public async wachtOpOpslagronde(limietMs: number): Promise<boolean> {
        if (!this.lopendeOpslagronde) {
            return true;
        }
        return Promise.race([
            this.lopendeOpslagronde.then(() => true),
            new Promise<boolean>(klaar => setTimeout(() => klaar(false), limietMs))
        ]);
    }

    /**
     * Meldt de simulatiekant aan; de `SimulatieService` doet dat bij zijn constructie.
     */
    public registreerSimulatieAansturing(aansturing: SimulatieAansturing): void {
        this.simulatie = aansturing;
    }

    /* ******** De dispatcher ******** */

    /**
     * De ingang voor de eigen gebruiker: voer deze actie uit. Een volger stuurt hem als verzoek naar
     * de leider zodra de tabel daar een vraagvorm voor kent; verder voert iedereen hem zelf uit.
     * @param actie de naam uit de actietabel
     * @param [args] de argumenten die de tabelregel verwacht
     */
    public doe(actie: string, args: Actieargumenten = {}): void {
        const regel = this.tabel.get(actie);
        if (!regel) {
            this.log.warn(`onbekende actie '${actie}'`);
            return;
        }
        if (this.dlModel.inReplay()) {
            return; //tijdens een replay verandert er niets aan het model
        }
        if (this.samenwerking.isVolger() && regel.vraag) {
            this.stuur([regel.vraag(args)]);
            return;
        }
        if (!regel.uitvoer) {
            this.log.warn(`actie '${actie}' heeft geen lokale uitvoering`);
            return;
        }
        this.voerUit(regel, () => regel.uitvoer!(args));
    }

    /**
     * Verwerkt een binnengekomen samenwerkbericht: de acties op volgorde, elk langs de tabel.
     *
     * Twee dingen staan aan de ingang. **De sanity-check**: het bericht moet over ons model gaan, we
     * moeten samenwerken, en de afzender moet de andere rol hebben dan wij — anders zouden een
     * leider en een volger elkaars uitvoeringen als verzoeken lezen. **De replay-guard**: wie
     * terugbladert door de geschiedenis mag het lopende model niet muteren, en zet zijn versie op 0
     * zodat hij ná de replay hoe dan ook herlaadt. Staan wij daarbij als leider te bladeren, dan
     * krijgt de vrager een weigering terug in plaats van stilte.
     *
     * Loopt de uitvoering bij een volger stuk, dan is onze graaf uit de pas met die van de leider.
     * Herladen is dan het hele antwoord: versie op 0 en de leider om een save vragen.
     */
    public ontvangSamenwerkbericht(bericht: Samenwerkbericht): void {
        const werkmodus = this.samenwerking.werkmodus();
        if (bericht.model !== this.opslag.notifyId() || werkmodus === 'lokaal' || werkmodus === bericht.brontype) {
            this.log.warn('Binnenkomend samenwerkbericht niet compatibel');
            return;
        }
        if (this.dlModel.inReplay()) {
            this.log.debug('samenwerkbericht genegeerd: er loopt een replay');
            this.opslag.zetVersie(0); //ná de replay hoe dan ook herladen
            if (this.samenwerking.isLeider()) {
                this.weigerVerzoek(bericht); //een volger die wacht, laten we niet in het ongewisse
            }
            return;
        }
        //de snapshotreeks van de leider bijhouden, ook als dit bericht verder niets voor ons doet:
        //krijgen wij straks de leiding, dan beginnen we boven wat hij al geclaimd heeft en houden we
        //zijn wachtdrempel aan
        this.opslag.zetSnapshotteller(bericht.snapshotvolgorde);
        this.opslag.zetSnapshotleeftijd(bericht.snapshotleeftijd);
        const context: Berichtcontext = {
            afzender: bericht.afzender,
            origineleAfzenderJij: !!bericht.originele_afzender_jij
        };
        const hadNogGeenNodes = this.dlModel.leeg();

        if (this.samenwerking.isLeider()) {
            for (const stap of bericht.acties ?? []) {
                this.verwerkVerzoek(stap, context);
            }
        } else {
            try {
                for (const stap of bericht.acties ?? []) {
                    if (!this.verwerkUitvoering(stap, context)) {
                        throw new Error(`ONBEKEND: ${stap.actie}`);
                    }
                }
                if (bericht.changeId) {
                    //niets stukgelopen: deze wijziging hoort bij de eerstvolgende save van de leider
                    this.opslag.samenwerkchange(bericht.changeId);
                }
            } catch (fout) {
                this.log.error('Fout bij het verwerken van een samenwerkbericht van de leider', fout);
                this.opslag.zetVersie(0); //dus altijd herladen
                this.stuur([{actie: 'directsave'}]); //svp meteen opslaan, dan kunnen wij herladen
            }
        }
        if (hadNogGeenNodes && !this.dlModel.leeg()) {
            this.nasleepMelding.meld(); //er staat nu iets: het canvas mag centreren
        }
    }

    /* ******** De simulatiebaan ******** */

    /**
     * Meldt het simcanvas-eiland aan. Komt van de `CanvasregistratieService`, die de canvassen al
     * bijhoudt: één aanmeldkanaal, geen tweede (werkregel 21-08-2026).
     */
    public registreerSimcanvas(eiland: Simulatie_eiland | null): void {
        this.simcanvas = eiland;
    }

    /**
     * Zet het simcanvas op slot of haalt het er weer af — voor de replay, die de simview readonly
     * meeneemt. Het eiland heeft de methodes al; de baas heeft het via `registreerSimcanvas`.
     */
    public zetSimviewReadonly(readonly: boolean): void {
        if (readonly) {
            this.simcanvas?.setReadonly();
        } else {
            this.simcanvas?.unsetReadonly();
        }
    }

    /**
     * Componeert de simulatiestatus in de wire-/snapshotvorm uit de vier eigenaren (golf 3d): het
     * simcanvas (simulatie, elementen, teller, de ineq-histories), de `SimulatieStatusService` (history), het model (de
     * value-history-scratch per quantity) en de `SchermindelingService` (de splitter-richting).
     * Aangeroepen door de snapshotbron en door `verstuurSimulatiestatus`. Sinds golf a5 bij de baas
     * (verhuisd van de migratie-interface, kernbesluit 9).
     *
     * Sinds 17-09-2026 zonder de simulatie zelf: de status draagt de invoerhash, en wie hem toepast haalt
     * de simulatie daarmee op (docs/projecten/2026-09-simulatieopslag/plan-simulatieopslag.md). Een simulatie van 65 MB brak anders de
     * verbinding, omdat hij in elk statusbericht en elke snapshot opnieuw de deur uit ging.
     * @param fit Als `true`, dan vraagt het bericht de ontvanger opnieuw te fitten
     * @param [metSimulatie] de simulatie er toch in zetten. Alleen voor een stand die deze client zelf
     *   bewaart (de replay bij het binnengaan), nooit voor iets dat over de lijn gaat
     */
    public haalSimulatiestatus(fit?: boolean, metSimulatie = false): SimulatieStatus {
        const stand = this.simcanvas?.haalSimulatiestand();
        //de teller reist altijd mee, ook als er niet gesimuleerd wordt
        const status: SimulatieStatus = {
            selectievolgorde: stand?.selectievolgorde ?? 0,
            simuleren: !!stand?.simulatie
        };
        if (!stand?.simulatie) {
            return status;
        }
        if (metSimulatie) {
            status.simulatie = stand.simulatie;
        }
        status.elements = stand.elements;
        status.invoerhash = stand.invoerhash;
        status.history = {...this.simulatieStatus.history()};
        status.valuehistories = {};
        for (const qNode of this.dlModel.quantities()) {
            status.valuehistories[qNode.id()] = {
                show: !!qNode.scratch('showvaluehistory'),
                lefttop: qNode.scratch('historylefttop')
            };
        }
        //een kopie, ook als de simulatie zelf meegaat: bij het ingaan van de replay bewaart de client
        //zijn stand mét een verwijzing naar hetzelfde simulatie-object, en de replaystappen zetten de
        //map daarvan om
        status.ineqhistories = structuredClone((stand.simulatie as SimulatieData).ineqHistory ?? {});
        status.resizeDirection = this.schermindeling.richting();
        status.simfeedback = this.haalSimfeedbackStand();
        if (fit) {
            status.fit = true;
        }
        return status;
    }

    /**
     * Stuurt de huidige simulatiestatus rond naar de samenwerkers. De leider-guard en het plannen liggen
     * bij de aanroeper (`DlSimCanvasComponent.planStatusVersturen`); tot golf 4f liep dit via de
     * migratie-interface.
     * @param fit Als `true`, dan vraagt het bericht de ontvanger opnieuw te fitten
     */
    public verstuurSimulatiestatus(fit?: boolean): void {
        this.stuurSim('simstatus', {status: this.haalSimulatiestatus(fit)});
    }

    /**
     * Wat er bij een vers document (`DlModel.tekenGraaf`) moet gebeuren buiten het tekenen zelf: de
     * simfeedback (data en selectie) hoort bij het vorige document en gaat weg — de toon-vlag komt uit
     * het document zelf — en daarna een verse bouwsuggestie-run. Geeft de promise van die run terug,
     * zodat het canvas zijn animatielock pas loslaat als die klaar is.
     * @param [aanleiding] waarom er getekend werd; de bouwsuggestie-run moet weten of dit een
     *   teruggehaalde stand is, want dan valt er niets nieuws te melden
     */
    private naGraafGetekend(aanleiding?: Tekenaanleiding): Promise<void> {
        this.pasSimfeedbackStatusToe(null);
        return this.runBouwsuggesties(aanleiding);
    }

    /**
     * Past een binnengekomen simulatiestatus toe: bij een volger na een `simstatus`-bericht, bij
     * replay vanuit de snapshot. Pakt de wire-vorm uit en zet elk deel bij zijn eigenaar neer. Bewust
     * niet-zendend en niet-loggend (golf 3c-lijn), zodat replay structureel veilig is en niet leunt op
     * een lock of op werkmodus-guards verderop in de keten.
     *
     * Draagt de status geen simulatie (sinds 17-09-2026 het gewone geval), dan wordt die eerst
     * opgehaald op de invoerhash. Pas daarna wordt er iets neergezet, en alleen als er intussen geen
     * nieuwere status of opruimactie kwam. Oude snapshots dragen de simulatie nog zelf en gaan zoals
     * altijd.
     * @param status De binnengekomen status; `null`/`undefined` ruimt de simulatie op
     * @param fit Forceer een fit, ook zonder `fit`-vlag in het bericht (replay)
     * @param [herkomst] waar de simfeedback erin vandaan komt; een replaystap is `teruggehaald`
     * @returns zodra de status is toegepast, opgeruimd of achterhaald
     */
    public async pasSimulatiestatusToe(status: SimulatieStatus | null | undefined, fit?: boolean,
                                       herkomst: Feedbackherkomst = 'vanLeider'): Promise<void> {
        const teller = ++this.statusteller;
        if (!status?.simuleren) {
            this.ruimSimulatiestatusOp(herkomst);
            return;
        }
        const simulatie = status.simulatie ?? await this.simulatieVoorHash(status.invoerhash ?? null);
        if (teller !== this.statusteller) {
            return; //er kwam intussen een nieuwere status
        }
        if (!simulatie) {
            this.log.warn('pasSimulatiestatusToe: de simulatie bij deze status is er niet (meer)', status.invoerhash);
            this.ruimSimulatiestatusOp(herkomst);
            return;
        }
        this.simulatieStatus.zetSimuleren(true);
        this.simulatieStatus.zetHistory(status.history ?? {value: false, ineq: false});
        for (const [qNodeId, his] of Object.entries(status.valuehistories ?? {})) {
            const qNode = this.dlModel.getElement(qNodeId);
            if (qNode.empty()) {
                this.log.warn('pasSimulatiestatusToe: quantity voor valuehistory niet gevonden', qNodeId);
                continue;
            }
            qNode.scratch('showvaluehistory', his.show);
            qNode.scratch('historylefttop', his.lefttop);
        }
        //de ineq-histories hangen aan de simulatie, en die komt sinds 17-09-2026 zonder de map van de
        //afzender binnen; de status draagt ze los mee. Staat het veld er niet (een snapshot van 17 tot
        //29-09-2026), dan is er over deze stap niets bekend. Een nog oudere snapshot draagt de simulatie
        //zelf, en daarmee zijn eigen map
        if (status.ineqhistories) {
            (simulatie as SimulatieData).ineqHistory = structuredClone(status.ineqhistories);
        } else if (!status.simulatie) {
            (simulatie as SimulatieData).ineqHistory = {};
        }
        this.simcanvas?.pasSimulatiestandToe({
            simulatie,
            elements: status.elements,
            selectievolgorde: status.selectievolgorde,
            invoerhash: status.invoerhash
        }, !!fit || !!status.fit);
        this.pasSimfeedbackStatusToe(status.simfeedback, herkomst);
    }

    /**
     * Ruimt de simulatiestand op: het scherm dicht, geen histories, geen simfeedback.
     * @param herkomst waar de (lege) simfeedback vandaan komt
     */
    private ruimSimulatiestatusOp(herkomst: Feedbackherkomst): void {
        this.simulatieStatus.zetSimuleren(false);
        this.simulatieStatus.zetHistory({value: false, ineq: false});
        this.simcanvas?.ruimSimulatieOp();
        this.pasSimfeedbackStatusToe(null, herkomst);
    }

    /**
     * De simulatie bij deze invoerhash: die op het simcanvas als hij dezelfde hash heeft, anders de
     * laatst opgehaalde, anders van het backend. `null` als er geen hash is of het backend hem niet
     * (meer) heeft.
     * @param hash de invoerhash uit de status
     */
    private async simulatieVoorHash(hash: string | null): Promise<unknown | null> {
        const modelid = this.opslag.modelId();
        if (!hash || !modelid) {
            return null;
        }
        const opCanvas = this.simcanvas?.haalSimulatiestand();
        if (opCanvas?.simulatie && opCanvas.invoerhash === hash) {
            return opCanvas.simulatie;
        }
        if (this.opgehaaldeSimulatie?.hash === hash) {
            return this.opgehaaldeSimulatie.simulatie;
        }
        const simulatie = await this.api.simulatieOphalen(modelid, hash);
        if (simulatie) {
            this.opgehaaldeSimulatie = {hash, simulatie};
        }
        return simulatie;
    }

    /**
     * De ingang voor de eigen gebruiker op de simulatiebaan: doe deze simulatieactie. Een volger
     * vraagt hem aan de leider, ieder ander voert hem uit.
     */
    public doeSim(actie: string, args: Actieargumenten = {}): void {
        const regel = this.simtabel.get(actie);
        if (!regel) {
            this.log.warn(`onbekende simulatieactie '${actie}'`);
            return;
        }
        if (this.dlModel.inReplay()) {
            return;
        }
        if (this.samenwerking.isVolger() && regel.vraag) {
            this.stuurSim(regel.vraag(args).actie, {});
            return;
        }
        if (regel.uitvoer) {
            this.voerUit(regel, () => regel.uitvoer!(args));
        }
    }

    /**
     * Verwerkt een binnengekomen simulatiebericht. Eén actie per bericht, en een tweede poging na
     * een seconde als de rollen nog niet kloppen: bij het opzetten van een samenwerking kan een
     * bericht vóór de rolverdeling aankomen, en dan is het na die tik wél te plaatsen.
     * @param bericht het binnengekomen bericht
     * @param [tweedePoging] intern; dan geven we het op
     */
    public ontvangSimulatiebericht(bericht: Simulatiebericht, tweedePoging?: boolean): void {
        if (this.dlModel.inReplay()) {
            this.log.debug('simulatiebericht genegeerd: er loopt een replay');
            return;
        }
        const wijLeiden = this.samenwerking.isLeider() && bericht.brontype === 'volg';
        const wijVolgen = this.samenwerking.isVolger() && bericht.brontype === 'leid';
        if (!wijLeiden && !wijVolgen) {
            if (tweedePoging) {
                this.log.warn('Ongeldig simulatiebericht', bericht);
                return;
            }
            setTimeout(() => this.ontvangSimulatiebericht(bericht, true), 1000);
            return;
        }
        const regel = this.simtabel.get(bericht.actie);
        const context: Berichtcontext = {afzender: bericht.afzender, origineleAfzenderJij: false};
        if (wijLeiden && regel?.lees && regel.uitvoer) {
            const args = regel.lees(bericht);
            if (args) {
                this.voerUit(regel, () => regel.uitvoer!(args, context.afzender ?? null), context.afzender);
            }
            return;
        }
        if (wijVolgen && regel?.vanLeider) {
            this.voerUit(regel, () => regel.vanLeider!(bericht, context));
            return;
        }
        this.log.debug(`simulatiebericht '${bericht.actie}' hoort niet bij deze rol`);
    }

    /**
     * Verwerkt als leider een simfeedback-verzoek van een volger: uitvoeren via de feedbacklagen,
     * terugsyncen en op naam van de volger loggen. Een toggle loopt langs de balk, die zijn eigen
     * staart meebrengt; een selectie zetten we hier en synct daarna mee.
     */
    private voerSimfeedbackactieUit(actie: string, arg: unknown, volger?: string): void {
        if (!this.samenwerking.isLeider()) {
            return;
        }
        switch (actie) {
            case 'toggle':
                this.feedbackbalk.voerToggleUit('sim', !!arg, volger);
                break;
            case 'highlight': {
                const selectie = arg as {type: string; index: number};
                this.feedbackSelectie.selecteerSimfeedback(selectie.type as never, selectie.index, 'ontvangen');
                this.syncSimfeedback();
                this.opslag.directlogAction('simfb_select', logargumenten(selectie), 'model', 'model', false, volger);
                this.opslag.markeerSnapshotNodig();
                break;
            }
        }
    }

    /**
     * Stuurt als leider de simfeedback-stand naar de volgers: of hij getoond wordt, wat de engine
     * uitrekende en wat er aangewezen staat.
     */
    public syncSimfeedback(): void {
        if (this.samenwerking.isLeider()) {
            this.stuurSim('simfeedback', {status: this.haalSimfeedbackStand()});
        }
    }

    /**
     * De simfeedback-stand in de wire-/snapshotvorm.
     */
    public haalSimfeedbackStand(): SimfeedbackStand {
        return {
            showSimfeedback: this.dlModel.simToon,
            feedback: this.simfeedbackEngine.feedback(),
            selectedsimfeedback: this.feedbackSelectie.selectie().sim
        };
    }

    /**
     * Past een binnengekomen simfeedback-stand toe (volger-sync, replay) of wist alles bij `null`
     * (de simulatie stopt, het model herlaadt). Bewust zonder verzenden of loggen — dat maakt de
     * route ook tijdens een replay veilig.
     * @param stand de simfeedback-stand, of `null` om alles te wissen
     * @param [herkomst] waar hij vandaan komt; een replaystap is `teruggehaald` en meldt geen nieuws
     */
    public pasSimfeedbackStatusToe(stand: SimfeedbackStand | null | undefined,
                                   herkomst: Feedbackherkomst = 'vanLeider'): void {
        this.feedbackSelectie.wisSelectie();
        if (!stand?.feedback) {
            //niets te tonen: leegmaken, en de vergelijking begint straks overnieuw
            this.simfeedbackEngine.reset();
        }
        if (!stand) {
            return;
        }
        //de toon-vlag is documentstate van de leider en wordt onvoorwaardelijk overgenomen, dus ook
        //`false`; anders bleef een volger op zijn oude stand hangen en stuurde hij bij een eigen
        //klik een verkeerde gewenste waarde naar de leider
        this.dlModel.simToon = !!stand.showSimfeedback;
        if (stand.feedback) {
            //bewust géén reset ervoor: `setFeedback` vervangt de hele uitslag, en een reset wist
            //alleen de boekhouding van wat er sinds de vorige uitslag bíj kwam. Met die reset telde
            //élke melding weer als nieuw, dus pulseerde bij de ontvanger het hele canvas — ook bij
            //een sync die alleen over een aangeklikte melding ging (bevinding testronde a4,
            //09-09-2026, test 15)
            this.simfeedbackEngine.setFeedback(stand.feedback, herkomst);
        }
        if (stand.selectedsimfeedback && this.simfeedbackEngine.numBeschikbaar() > 0) {
            this.feedbackSelectie.selecteerSimfeedback(stand.selectedsimfeedback.type,
                stand.selectedsimfeedback.index, 'ontvangen');
        }
    }

    /**
     * Verwerkt als leider één stap uit een verzoek van een volger. Geeft `false` terug als de tabel
     * de actie niet kent.
     */
    public verwerkVerzoek(stap: Samenwerkactie, context: Berichtcontext): boolean {
        const regel = this.tabel.get(stap.actie);
        if (!regel?.lees || !regel.uitvoer) {
            return false;
        }
        this.neemCondtagOver(stap);
        const args = regel.lees(stap);
        if (!args) {
            this.log.warn(`verzoek '${stap.actie}' geskipt: argumenten niet te matchen`, stap);
            return true; //wel van ons, maar niet uit te voeren
        }
        this.voerUit(regel, () => regel.uitvoer!(args, context.afzender ?? null), context.afzender);
        return true;
    }

    /**
     * Verwerkt als volger één uitvoering van de leider. Geeft `false` terug als de tabel de actie
     * niet kent. Gooit door bij `UNMATCH`: dan loopt onze graaf uit de pas en is herladen het enige
     * antwoord — de aanroeper handelt dat af.
     */
    public verwerkUitvoering(stap: Samenwerkactie, context: Berichtcontext): boolean {
        const regel = this.tabel.get(stap.actie);
        if (!regel?.vanLeider) {
            return false;
        }
        this.neemCondtagOver(stap);
        this.voerUit(regel, () => regel.vanLeider!(stap, context));
        return true;
    }

    /**
     * De vaste staart om elke actie heen: **undo-punt → uitvoer → logregels → bericht → melding dat er
     * een snapshot nodig is → opslagronde**. Die volgorde stond in AngularJS vijftien keer met de hand
     * uitgeschreven, met per keer kleine verschillen; hier staat hij één keer.
     *
     * Het bericht gaat alleen de deur uit als wij leider zijn: een volger die een uitvoering van de
     * leider overneemt stuurt hem niet terug.
     */
    private voerUit(regel: Actieregel, uitvoerder: () => Actieuitkomst, origineleAfzender?: string): void {
        if (regel.undo) {
            this.opslag.setUndoPoint();
        }
        const uitkomst = uitvoerder();
        const geschreven = (uitkomst.logregels ?? []).map(intentie => this.opslag.schrijfLog(intentie));
        if (uitkomst.bericht?.length && this.samenwerking.isLeider()) {
            this.stuur(uitkomst.bericht, origineleAfzender);
        }
        if (uitkomst.simbericht && this.samenwerking.isLeider()) {
            this.stuurSim(uitkomst.simbericht.actie, uitkomst.simbericht.args);
        }
        const direct = uitkomst.directlog
            ? this.opslag.directlogAction(uitkomst.directlog.actie, logargumenten(uitkomst.directlog.payload),
                uitkomst.directlog.doel ?? 'model', uitkomst.directlog.doel ?? 'model',
                false, uitkomst.directlog.volger)
            : null;
        if (uitkomst.inhoudGewijzigd && !geschreven.length) {
            this.opslag.meldInhoudGewijzigd();
        }
        //een actie met een logregel vraagt standaard om een snapshot; de uitkomst kan dat bijstellen.
        //Er wordt hier niets vastgelegd: de snapshot volgt als het model tot rust gekomen is, dus ná de
        //opslagronde hieronder en de rekenronde die daaruit volgt
        if (uitkomst.snapshot ?? !!(geschreven[geschreven.length - 1] ?? direct)) {
            this.opslag.markeerSnapshotNodig();
        }
        if (uitkomst.opslaan) {
            this.startOpslagronde(uitkomst.opslaan === 'relevant', uitkomst.direct, uitkomst.aanleiding);
        }
    }

    /**
     * Start de opslagronde en houdt hem vast zolang hij loopt. De aanroepers wachten er bewust niet
     * op — de actie is klaar — maar een replaystart moet dat wel kunnen (`wachtOpOpslagronde`).
     */
    private startOpslagronde(relevant: boolean, direct?: boolean, aanleiding?: Tekenaanleiding): void {
        const belofte = this.voerOpslagrondeUit(relevant, direct, aanleiding)
            .catch(fout => this.log.warn('opslagronde mislukt', fout));
        this.lopendeOpslagronde = belofte;
        void belofte.then(() => {
            if (this.lopendeOpslagronde === belofte) {
                this.lopendeOpslagronde = null;
            }
        });
    }

    /**
     * De opslagronde zelf: bij een inhoudelijke wijziging eerst een verse bouwsuggestie-run, dan de stand
     * van het canvas naar de opslagketen. Die bepaalt zelf of er echt opgeslagen wordt (de blokkade
     * tijdens het opbouwen van een graaf, een volger) en haalt het voorbeeldplaatje bij zijn tekenaar.
     *
     * Het werk begint pas ná de meldronde waarin de ronde gestart werd. Een wijziging van de
     * modeleigenschappen start hem vanuit een luisteraar op `bijPropsGewijzigd`, en op diezelfde melding
     * sluit een andere luisteraar de simulatie; zolang die open staat, slaat de bouwsuggestie-run zich
     * over. Alle luisteraars van een `Melding` lopen synchroon, dus na één tik is dat gebeurd, welke
     * luisteraar er ook eerst stond (toetsbevinding T1 van golf 4d). In AngularJS gaf de volgorde van de
     * broadcast dat vanzelf: de hoofdcontroller vóór de modelview.
     * @param relevant of het een inhoudelijke wijziging is
     * @param [direct] zonder de gebruikelijke vertraging opslaan
     * @param [aanleiding] waarom de stand veranderde; een teruggehaalde stand meldt geen nieuwe suggesties
     * @returns de uitkomst van de save, of `false` als er niet opgeslagen werd
     */
    private async voerOpslagrondeUit(relevant: boolean, direct?: boolean, aanleiding?: Tekenaanleiding): Promise<boolean | string> {
        await Promise.resolve();
        if (this.dlModel.fullReadonly) {
            return false;
        }
        if (relevant) {
            await this.runBouwsuggesties(aanleiding);
        }
        return this.opslag.save(this.dlModel.modelData(), relevant, direct);
    }

    /* ******** De bouwsuggesties en de uitleg ******** */

    /**
     * Een bouwsuggestie meldt een gebaar dat in de actielog hoort, en soms ook een wijziging aan het
     * model. Eén uitzondering op de gewone weg: het antwoord op een suggestie gaat bij een volger
     * altijd naar de leider — die houdt de actielog bij (ook van antwoorden die alleen de eigen
     * volgorde raken) en voert bij status 2 de regelstatus uit. De volger zet zelf niets.
     */
    public bouwsuggestieActie(actiedata: Bouwsuggestieactie): void {
        const handle = actiedata.args?.['handle'];
        if (this.samenwerking.isVolger() && actiedata.type === 'sug_antwoord' && actiedata.node && handle) {
            this.doe('bsAntwoord', {
                node: actiedata.node,
                handle,
                antwoord: actiedata.args?.['antwoord'],
                status: actiedata.args?.['status']
            });
            return;
        }
        this.voerUit({undo: !!actiedata.wijziging}, () => ({
            logregels: [this.opslag.legLogVast(actiedata.type,
                actiedata.node ? this.dlModel.route(actiedata.node) : null,
                (actiedata.node?.data('type') as string) ?? null, actiedata.args ?? {}, null, false)],
            //de stand leggen we alleen vast als de suggestie zegt dat er iets te zien is
            snapshot: !!actiedata.visuelewijziging,
            opslaan: actiedata.wijziging ? 'relevant' : null
        }));
    }

    /**
     * Een uitleg meldt een gebaar: alleen loggen. Geen wijzigingsmelding, want die zou de simulatie
     * sluiten.
     */
    public uitlegActie(type: string, node?: NodeSingular | null): void {
        this.opslag.logaction(type, node ? this.dlModel.route(node) : null, null,
            (node?.data('type') as string) ?? null, {}, false);
    }

    /**
     * Neemt de conditieset over die in een binnengekomen stap meekomt. De leider bepaalt waar een
     * element landt; is onze eigen weergave sinds het versturen verschoven, dan zou het anders in de
     * verkeerde set terechtkomen.
     */
    private neemCondtagOver(stap: Samenwerkactie): void {
        if (!('useCondtag' in stap)) {
            return;
        }
        const gevraagd = stap['useCondtag'] as CondtagState | null;
        const huidig = this.condtagService.condtag();
        if (gevraagd?.tag === huidig?.tag && gevraagd?.state === huidig?.state) {
            return;
        }
        this.condtagService.zetActieveTag(gevraagd ?? null);
    }

    /* ******** Verzenden ******** */

    /**
     * Stuurt een samenwerkbericht. Van een volger is het een verzoek aan de leider, van een leider
     * de melding dat de actie is uitgevoerd. Een leider stuurt een changeId mee — dat legt hij ook
     * bij de opslagketen vast, zodat de volger bij de eerstvolgende save kan zien of hij alles
     * gehad heeft — plus zijn undostand, want de knoppen van de volger tonen die van de leider.
     * @param acties de acties, in de volgorde waarin ze moeten lopen
     * @param [origineleAfzender] de client die de reeks begon; alleen als leider door te geven
     */
    public stuur(acties: Samenwerkactie[], origineleAfzender?: string): Promise<unknown> | void {
        const werkmodus = this.samenwerking.werkmodus();
        if (werkmodus === 'lokaal') {
            return; //niets te melden
        }
        if (this.dlModel.fullReadonly) {
            this.log.debug('samenwerkbericht geskipt wegens fullReadonly');
            return;
        }
        const isLeider = werkmodus === 'leid';
        let changeId: string | false = false;
        let extra: unknown = {};
        if (isLeider) {
            changeId = `${this.api.unique}-${Date.now().toString(16)}`;
            this.opslag.samenwerkchange(changeId); //bij de volgende save melden dat deze erbij hoort
            //de stand van onze snapshotreeks reist mee: een volger die hierna de leiding krijgt, telt
            //daarboven verder en houdt de wachtdrempel aan vanaf onze laatste snapshot. Bij het nummer
            //bewust het vólgende en niet het laatst gebruikte: de snapshot van deze actie komt pas als
            //het model tot rust gekomen is, en dat nummer hoort dan al als bezet te gelden
            extra = {
                canUndo: this.opslag.canUndo(), canRedo: this.opslag.canRedo(),
                snapshotvolgorde: this.opslag.volgendeSnapshotvolgorde,
                snapshotleeftijd: this.opslag.snapshotleeftijd
            };
        }
        return this.api.samenwerkbericht(werkmodus, changeId, this.opslag.modelId(),
            this.opslag.notifyId(), origineleAfzender, acties, extra);
    }

    /**
     * Weigert als leider de verzoeken uit één bericht, omdat wij zelf terugbladeren.
     *
     * Gaat bewust buiten `stuur()` om: die zwijgt op `fullReadonly`, en dat is nu juist de stand
     * waarin dit bericht moet vertrekken. Dezelfde splitsing als bij de registratie in golf a4 — het
     * protocol mag praten over de readonly-stand, ook al mag het model niet muteren. Geen changeId en
     * geen undostand: er is niets uitgevoerd.
     *
     * De weigering noemt naar buiten geen replay. Wie terugbladert is privé (besluit 09-09-2026); de
     * reden reist alleen mee voor de log aan de andere kant.
     *
     * Het venster hiervoor is smal: het backend weigert een leiderbericht zodra het ons al volger
     * noemt. Dat is dan de normale afloop, en dus een debugregel en geen melding.
     * @param bericht het verzoek dat we niet uitvoeren
     */
    private weigerVerzoek(bericht: Samenwerkbericht): void {
        const acties: Samenwerkactie[] = (bericht.acties ?? [])
            .map(stap => ({actie: 'verzoekGeweigerd', verzoek: stap.actie, reden: 'replay'}));
        if (!acties.length) {
            return;
        }
        this.api.samenwerkbericht('leid', false, this.opslag.modelId(), this.opslag.notifyId(),
            bericht.afzender, acties, {})
            .catch((fout: unknown) => this.log.debug('weigering niet verstuurd', fout));
    }

    /**
     * Stuurt een simulatiebericht. Tweede baan van hetzelfde protocol: eigen kanaal, zelfde
     * leider/volger-logica. Het backend weigert een volger-bericht zodra er een tweede volger
     * meedoet (bevinding bij golf 3d); dat loggen we, zodat het zichtbaar blijft zolang dat speelt.
     *
     * Wie `lokaal` werkt, zit alleen op het model: er is niemand om het bericht naar te sturen, en het
     * backend vond ook niemand. Dan gaat er niets de deur uit (testronde 20-09-2026: een lokaal model
     * stuurde bij elke simulatie twee `simbezig`-berichten). Wie later aansluit, krijgt de stand via
     * de sync.
     * @param actie de simulatie-actienaam
     * @param [args] wat er bij die actie hoort
     */
    public stuurSim(actie: string, args?: Record<string, unknown>): void {
        if (this.samenwerking.werkmodus() === 'lokaal') {
            return;
        }
        this.api.simulatiebericht({
            brontype: this.samenwerking.werkmodus(),
            actie,
            actieId: `${this.api.unique}-${Date.now().toString(16)}`,
            modelId: this.opslag.modelId(),
            notifyId: this.opslag.notifyId(),
            ...(args ?? {})
        }).catch((fout: unknown) => this.log.warn(`simulatiebericht '${actie}' geweigerd`, fout));
    }

    /**
     * Vraagt als volger een volledige bijwerking aan de leider. Voor de terugkeer uit een replay: wij
     * hebben zelf niets gerekend, en bij een herregistratie op hetzelfde model stuurt de leider uit
     * zichzelf niets. Een markering hoeft er niet bij — sinds golf a4 stap 7 draagt een sync vanzelf
     * geen nieuws, dus er pulseert niets.
     */
    public vraagSync(): void {
        if (this.samenwerking.isVolger()) {
            this.stuur([{actie: 'sync'}]);
        }
    }

    /**
     * Meldt de eigen undostand aan de volgers. Alleen een leider heeft daar iets te melden.
     */
    public verzendUndostatus(): void {
        if (this.samenwerking.isLeider()) {
            void this.api.samenwerkUndostatus(this.opslag.canUndo(), this.opslag.canRedo());
        }
    }

    /**
     * Stuurt als leider de norminterpretaties en de telling naar de volgers.
     *
     * Wat er bij déze uitslag nieuw is gaat als argument mee en wordt niet uit de servicestand
     * gelezen: dit bericht vertrekt langs drie wegen — een verse telling, een verse uitslag, en het
     * bijbrengen van een nieuwe deelnemer — en alleen bij de tweede valt er nieuws te melden. Zou de
     * verzendkant het zelf opzoeken, dan kreeg een deelnemer die er middenin bij komt het nieuws van
     * de vorige ronde alsnog te zien.
     * @param nieuw de foutsleutels die bij deze uitslag nieuw zijn
     */
    public stuurNorm(nieuw: string[] = []): void {
        if (this.dlModel.fullReadonly) {
            this.log.debug('stuurNorm geskipt wegens fullReadonly');
            return;
        }
        if (!this.samenwerking.isLeider()) {
            return;
        }
        this.stuur([{
            actie: 'norm',
            interpretaties: this.normService.norminterpretaties(),
            telling: {
                telling: this.normService.telling(),
                naamtelling: this.normService.naamtelling()
            },
            nieuw
        }]);
    }

    /**
     * Stuurt als leider alles wat een nieuwe of net gepromoveerde deelnemer nodig heeft om bij te
     * zijn. Dat zijn vijf lijnen, niet vier: de undostand, de norm, de conditiesets, een verse
     * bouwsuggestie-run en de simulatiestatus. De laatste blijft bij de planner van het
     * simcanvas-eiland, dat hem bewust met een tik vertraging stuurt (werkregel 21-08-2026).
     */
    public syncNaarVolgers(): void {
        if (!this.samenwerking.isLeider()) {
            return;
        }
        this.verzendUndostatus();
        this.stuurNorm();
        this.deelUxStand();
    }

    /**
     * Stuurt als leider de gedeelde weergavestand: welke conditieset actief is en welke er zichtbaar
     * zijn, plus een verse bouwsuggestie-run — die stuurt zijn uitkomsten via `bsSync` mee. Apart van
     * `syncNaarVolgers`, omdat een werkmoduswissel de norm langs een eigen weg regelt en die anders
     * twee keer zou vertrekken.
     *
     * Of het paneel openstaat gaat sinds 09-09-2026 niet meer mee: dat is lokaal gedrag.
     */
    private deelUxStand(): void {
        this.stuur([{
            actie: 'syncConditiesets',
            condtag: this.condtagService.condtag(),
            conditionele_tags: this.condtagService.conditioneleTags()
        }]);
        void this.runBouwsuggesties();
    }

    /**
     * Draait de bouwsuggestie-engine als dat hier van toepassing is, en stuurt als leider de
     * uitkomsten naar de volgers. Drie redenen om het niet te doen: het project heeft geen
     * bouwsuggesties of de simulatie staat open, er loopt een replay, of wij zijn volger — die
     * rekent nooit zelf, hij krijgt de stand via `bsSync`.
     *
     * De promise resolvet als de run klaar is; daar hangt de opslagronde aan, en het canvas wacht
     * erop voordat het zijn animatielock loslaat.
     * @param [aanleiding] waarom er gerund wordt. Bij een teruggehaalde stand — een undo, een redo,
     *   een replaystap — valt er per definitie niets nieuws te melden
     */
    public async runBouwsuggesties(aanleiding?: Tekenaanleiding): Promise<void> {
        if (!this.projectVlaggen.bouwsuggesties() || this.simulatieStatus.simuleren()
            || this.dlModel.inReplay() || this.samenwerking.isVolger()) {
            return;
        }
        await this.bouwsuggestieEngine.run(herkomstVanAanleiding(aanleiding));
        if (!this.samenwerking.isLeider()) {
            return;
        }
        //afgeschermd, want aan deze promise hangt de opslagketen en die mag hier nooit op stuklopen
        try {
            this.stuur([{actie: 'bsSync', data: this.bouwsuggestieEngine.exporteerRun()}]);
        } catch (fout) {
            this.log.error('bsSync versturen mislukt', fout);
        }
    }

    /* ******** Registreren ******** */

    /**
     * Meldt dit model bij het backend aan voor samenwerking, of af. De registratie is idempotent en
     * wordt bij elke modelwissel, na elk registratieverzoek en na de eerste opslag opnieuw gedaan;
     * het backend verdeelt daarop de rollen opnieuw.
     *
     * De `fullReadonly`-guard die ng1 hier had is gesplitst (bevinding 7): **registreren mag
     * altijd**, ook tijdens een replay — anders valt een replayende client na een hapering uit de
     * groep en krijgt hij geen chat en geen notificaties meer. Wat dicht blijft zijn de
     * mutatiepaden, en die guarden zelf.
     * @param [deregistreer] meld juist af
     */
    public async registreerSamenwerken(deregistreer?: boolean): Promise<boolean> {
        const notifyId = this.opslag.notifyId();
        try {
            const uitkomst = await this.api.registreerSamenwerken(
                deregistreer ? null : notifyId,
                (!deregistreer) && this.samenwerking.meekijkmodel() ? this.opslag.modelId() : null,
                this.dlModel.inReplay());
            if (uitkomst?.model === (deregistreer ? null : notifyId)) {
                //nog steeds hetzelfde model: de rol die we terugkregen geldt
                this.zetWerkmodus(uitkomst.werkmodus);
            }
            return true;
        } catch (fout) {
            this.log.debug('registreerSamenwerken niet gelukt', fout);
            return false; //niet ingelogd, of geen verbinding
        }
    }

    /* ******** Binnenkomend ******** */

    /**
     * Zet de notificatiestromen op. Alles wat het samenwerkprotocol raakt komt hier binnen; de
     * dashboard- en chatnotificaties hebben hun eigen afnemers.
     */
    private luisterNaarNotificaties(): void {
        this.api.onNotify('registratieVerzoek').subscribe(() => void this.opRegistratieVerzoek());
        this.api.onNotify('clientOnline').subscribe(() => this.opClientOnline());
        this.api.onNotify<ClientnamesNotify>('clientnames').subscribe(data => this.opClientnames(data));
        this.api.onNotify<WerkmodusNotify>('wijzigSamenwerkmodus').subscribe(data => this.opWerkmodus(data));
        this.api.onNotify<Samenwerkbericht>('samenwerkbericht').subscribe(bericht => this.ontvangSamenwerkbericht(bericht));
        this.api.onNotify<Simulatiebericht>('simulatiebericht').subscribe(bericht => this.ontvangSimulatiebericht(bericht));
        this.api.onNotify('resetSamenwerking').subscribe(() => void this.opslag.herlaadUitBackend());
        this.api.onNotify<{canUndo: boolean; canRedo: boolean}>('undostatus')
            .subscribe(status => this.opslag.zetLeiderUndostatus(status));
        this.api.onNotify<ModelchangedNotify>('modelchanged').subscribe(data => void this.opModelchanged(data));
    }

    /**
     * Een ander heeft ons model opgeslagen. Een volger die samenwerkdata krijgt op de versie waar hij
     * zelf op staat, kijkt of hij alle wijzigingen van die save al had; zo ja, dan neemt hij alleen de
     * nieuwe versie over, anders herlaadt hij. In elk ander geval is herladen het hele antwoord — zonder
     * samenwerkdata (het backend laat die weg als er een save van een ander tussen zat) valt er niets te
     * vergelijken. Eerst wachten tot een eigen save klaar is: die kan de versie nog verschuiven.
     * Tot golf 4d deed de AngularJS-modelview dit.
     */
    private async opModelchanged(data: ModelchangedNotify): Promise<void> {
        await this.opslag.onNotSaving();
        const notifyId = this.opslag.notifyId();
        if (!(this.opslag.modelId() && notifyId && notifyId === data.model)) {
            return;
        }
        const samenwerkdata = data.samenwerking;
        if (!(this.samenwerking.isVolger() && samenwerkdata?.changes && samenwerkdata.vanVersie === this.opslag.versie())) {
            await this.opslag.herlaadUitBackend('samenwerk-herlading', false);
            return;
        }
        if (samenwerkdata.changes.every(change => this.opslag.changes().includes(change))) {
            //alles al gehad: de lijst begint opnieuw, en we staan op de versie van de leider
            this.opslag.resetSamenwerkchanges();
            this.opslag.zetVersie(samenwerkdata.naarVersie);
        } else {
            await this.opslag.herlaadUitBackend('samenwerk-herlading', false);
        }
    }

    /**
     * Het backend vraagt om een nieuwe registratie. Dat gebeurt na een connect, want een client
     * heeft niet altijd door dat de verbinding weg is geweest. Een volger herlaadt daarna: alles
     * wat de leider intussen deed ging langs hem heen, en een gemiste `modelchanged` komt niet
     * alsnog. Een leider doet meteen zijn achterstallige opslagwerk.
     */
    private async opRegistratieVerzoek(): Promise<void> {
        this.log.debug('Backend vraagt om registratie');
        await this.registreerSamenwerken();

        if (this.samenwerking.isVolger()) {
            this.log.debug('Herladen model na herregistratie als volger');
            await this.opslag.herlaadUitBackend('samenwerk-herlading', false);
            return; //een volger slaat niet op; dat doet de leider
        }
        if (this.opslag.isOpgeslagen()) {
            this.log.debug('Opslaan model na herregistratie');
            await this.opslag.saveModel(false); //zonder nieuw voorbeeldplaatje
        }
        //de verbinding is terug en daarmee het readonly voorbij: als leider of lokaal hoort daar een
        //snapshot bij, van de stand van nu
        this.opslag.markeerSnapshotNodig();
    }

    /**
     * Er is een client bij gekomen. Als leider brengen we hem bij; een geopende samenwerkdialoog
     * haalt zijn stand opnieuw op.
     */
    private opClientOnline(): void {
        this.syncNaarVolgers();
        this.samenwerking.meldVerversen();
    }

    /**
     * De samenstelling van de groep is gewijzigd. Onszelf laten we uit de lijst.
     */
    private opClientnames(data: ClientnamesNotify): void {
        if (data.model !== this.opslag.notifyId()) {
            this.samenwerking.zetAanwezigen([]);
            return;
        }
        const eigenNaam = this.sessie.displaynaam();
        this.samenwerking.zetAanwezigen((data.names ?? []).filter(aanwezige => aanwezige.naam !== eigenNaam));
    }

    /**
     * Het backend heeft de rollen opnieuw verdeeld.
     */
    private opWerkmodus(data: WerkmodusNotify): void {
        if (data.model !== this.opslag.notifyId() || data.werkmodus === this.samenwerking.werkmodus()) {
            return;
        }
        //de verdeler stuurt de stand van de snapshotreeks mee. Wie hier leider wordt, moet zijn
        //volgnummers boven alles beginnen wat er al ligt en de wachtdrempel aanhouden vanaf de laatste
        //snapshot — ook als hij de laatste berichten miste
        this.opslag.zetSnapshotteller(data.snapshotvolgorde);
        this.opslag.zetSnapshotleeftijd(data.snapshotleeftijd);
        //wie leidde heeft de normmatch zelf gemaakt, en een volger heeft die van zijn leider gekregen: die
        //delen we. Opnieuw rekenen op hetzelfde model kan door de montecarlo-zoektocht een fout op een
        //andere node leggen, en dan was het eerste screenshot van de nieuwe leider een stap die niemand zette
        const hadNormmatch = !this.samenwerking.isVolger() || !!this.normService.normmatch();
        this.zetWerkmodus(data.werkmodus);
        this.samenwerking.meldVerversen();

        if (this.samenwerking.isVolger()) {
            return; //een volger krijgt de norm van zijn leider
        }
        if (this.dlModel.inReplay()) {
            //sluit de laatste ander zijn model terwijl wij bladeren, dan worden we hier lokaal. Een
            //verse ronde zou de teruggebladerde stand van het canvas lezen; bij de terugkeer rekenen
            //we hoe dan ook, want de standsleutel is dan al ongeldig
            this.log.debug('werkmoduswissel tijdens een replay: geen normronde');
            return;
        }
        if (hadNormmatch) {
            this.log.debug('Normmatch al bekend -> sturen');
            this.stuurNorm();
        } else {
            this.log.debug('Normmatch nog niet bij ons -> maken');
            this.opslag.maakNormmatch(); //die meldt straks, en dán vertrekt de norm
        }
        //een verse leider deelt ook zijn weergavestand: de volgers keken naar die van een ander
        this.deelUxStand();
    }

    /**
     * Neemt een nieuwe werkmodus over en meldt hem. Een verse leider vertelt meteen wat er bij hem
     * te undo'en valt: de knoppen van de volgers tonen zijn stand.
     *
     * Wie ophoudt volger te zijn, neemt eerst zijn canvas over als documentstand. Een volger slaat niet
     * op, dus zijn stand is die van zijn laatste herlading, terwijl zijn canvas bijhield wat de leider
     * deed. Vanaf nu is hij zelf de bron: een normronde op die oude stand gaf een lege uitslag, en een
     * snapshot zonder foutmodel (test 7 van test-golf-a5.md). Tijdens een replay niet, want dan staat er
     * een teruggebladerde stand op het canvas; het stoppen herlaadt toch.
     *
     * Daarna meldt hij dat er een snapshot nodig is. Wat daarop volgt is de stand van dát moment en niet
     * wat hij als volger gemist heeft, en zo hoort een screenshot van een rusttoestand ook te zijn. De
     * vlag reist bewust niet over de lijn.
     */
    private zetWerkmodus(werkmodus: Werkmodus): void {
        if (werkmodus === this.samenwerking.werkmodus()) {
            return;
        }
        const wasVolger = this.samenwerking.isVolger();
        this.samenwerking.zetWerkmodus(werkmodus);
        const geenVolgerMeer = wasVolger && !this.samenwerking.isVolger();
        if (geenVolgerMeer && !this.dlModel.inReplay()) {
            this.opslag.zetStand(this.dlModel.modelData());
            this.opslag.addMeta();
        }
        if (this.samenwerking.isLeider()) {
            this.verzendUndostatus();
        }
        if (geenVolgerMeer) {
            this.opslag.markeerSnapshotNodig();
        }
    }

    /* ******** Wat de andere diensten melden ******** */

    /**
     * De opslagketen meldt wat het protocol aangaat: een gewijzigde undostand gaat naar de volgers,
     * en na de eerste opslag heeft het model een id en hoort het geregistreerd te worden.
     */
    private luisterNaarDeOpslag(): void {
        this.opslag.bijUndostatus(status => {
            if (this.samenwerking.isLeider()) {
                void this.api.samenwerkUndostatus(status.canUndo, status.canRedo);
            }
        });
        //de afloop telt: het plaatje mag pas vertrekken als de registratie staat
        this.opslag.bijEersteOpslag(async () => {
            await this.registreerSamenwerken();
        });
        this.opslag.bijPropsGewijzigd(wijziging => this.naPropsGewijzigd(wijziging));
    }

    /**
     * De modeleigenschappen zijn gezet. Het effectieve modelleerniveau gaat op alle nodes, en behalve in
     * readonly gaat de wijziging naar de andere kant — als leider altijd, als volger alleen als hij niet
     * juist van de leider kwam (`notifyArg === 'leid'`, de rij `modelprops`) — met een opslagronde erna.
     * Tot golf 4d deed de AngularJS-modelview dit op `model.propsChanged`.
     */
    private naPropsGewijzigd(wijziging: Propswijziging): void {
        this.dlModel.zetModelleerniveauOpNodes();
        if (this.dlModel.fullReadonly) {
            return;
        }
        if (this.samenwerking.isLeider() || (this.samenwerking.isVolger() && wijziging.notifyArg !== 'leid')) {
            this.stuur([{actie: 'modelprops', props: wijziging.gewijzigd}]);
        }
        this.startOpslagronde(true);
    }

    /**
     * De norm meldt verse interpretaties; als leider sturen we die door. De norm kent zijn afnemers
     * niet — zou hij ons aanroepen, dan injecteerde hij de baas terwijl wij hém injecteren.
     */
    private luisterNaarDeNorm(): void {
        this.normService.bijNormdata(melding => this.stuurNorm(melding.nieuw));
    }

    /**
     * De feedbacklagen (de balk met zijn vier toggles, en de selectie van een fout, een suggestie of
     * een simfeedback-loop) doen hun eigen state en vragen ons alleen om de route: als volger een
     * verzoek aan de leider, anders uitvoeren, doorsturen en loggen.
     */
    private luisterNaarDeFeedback(): void {
        this.feedbackbalk.initialiseer({
            vraagToggle: (soort, toon) => this.vraagToggle(soort, toon),
            meldToggle: (soort, toon, volger) => this.naToggle(soort, toon, volger)
        });
        this.feedbackSelectie.initialiseer({
            //een volger vraagt het de leider en zet zelf niets; die voert uit, logt op onze naam en
            //duwt de selectie terug — het bericht bereikt ook de aanvrager weer
            vraagFoutselectie: bericht => this.stuur([
                {actie: 'highlight_fout', elementId: bericht.elementId, foutcode: bericht.foutcode}]),
            vraagBouwsuggestieselectie: bericht => this.stuur([
                {actie: 'highlight_bouwsuggestie', node: bericht.nodeId, regel: bericht.handle, soort: bericht.soort}]),
            meldFoutselectie: bericht => this.meldSelectie(
                {actie: 'highlight_fout', elementId: bericht.elementId, foutcode: bericht.foutcode},
                'foutselect', bericht.log),
            meldBouwsuggestieselectie: bericht => this.meldSelectie(
                {actie: 'highlight_bouwsuggestie', node: bericht.nodeId, regel: bericht.handle, soort: bericht.soort},
                'sug_select', bericht.log),
            //de sim-selectie loopt over de simulatielijn: de volger zet zelf niets — hij vraagt het
            //de leider, die toepast, terugsynct en logt
            vraagSimSelectie: desc => this.stuurSim('simfeedbackactie',
                {feedbackactie: 'highlight', feedbackarg: desc}),
            meldSimSelectie: desc => {
                this.syncSimfeedback();
                this.opslag.directlogAction('simfb_select', logargumenten(desc), 'model', 'model');
                this.opslag.markeerSnapshotNodig();
            },
            //het intrekken van een aanwijzing gaat dezelfde weg als het aanwijzen: rechtstreeks de
            //deur uit, en de leider stuurt hem door naar de andere volgers
            meldSelectieGewist: () => this.stuur([{actie: 'wisFeedbackselectie'}])
        });
    }

    /**
     * De transport- en logketens van het simcanvas. Het eiland rekent en tekent; wat het aan ons
     * vraagt is de route: als volger een verzoek aan de leider, anders uitvoeren, doorsturen en
     * loggen. De simulatie logt altijd direct — er wordt tijdens een simulatie niet opgeslagen.
     *
     * Het openen en sluiten van het scherm zelf zit hier niet bij: dat leest het bouwscherm uit de
     * `SimulatieStatusService`.
     */
    private luisterNaarHetSimcanvas(): void {
        this.simulatieStatus.initialiseer({
            vraagSelectie: selectie => this.stuurSim('setSelected', {
                selectievolgorde: selectie.selectievolgorde,
                selected: selectie.selected
            }),
            meldSelectie: (logdata, volger) => {
                //direct loggen, anders vertrekt het pas als de simulatie weer dicht is en wordt het
                //vaak overschreven. Bij precies één toestand loggen we de details
                if (logdata.enkel) {
                    this.opslag.directlogAction('sim_select', logargumenten(logdata.enkel), 'sim', 'sim', false, volger);
                } else {
                    this.opslag.directlogAction('sim_multiselect', logargumenten({ids: logdata.ids}), 'sim', 'sim', false, volger);
                }
                this.opslag.markeerSnapshotNodig();
            },
            meldPosities: posities => {
                if (!this.samenwerking.isVolger()) {
                    this.opslag.directlogAction('sim_position', {}, 'sim', 'sim');
                    this.opslag.markeerSnapshotNodig();
                }
                if (this.samenwerking.werkmodus() !== 'lokaal') {
                    this.stuurSim('positie', {posities});
                }
            },
            vraagVerbergValuehistory: historyNodeId => this.stuurSim('hideValueHistory', {historyNode: historyNodeId}),
            vraagVerbergIneqhistory: ineqkey => this.stuurSim('hideIneqHistory', {ineqkey})
        });
    }

    /**
     * De knoppen van het conditiesetpaneel. Het paneel muteert zijn state niet zelf: elke klik komt
     * hier binnen en gaat als actie de tabel in, zodat de samenwerking, de undo, het log en het
     * opslaan er langs dezelfde weg aan hangen als bij elke andere modelactie.
     *
     * Drie kliks doen niets als ze niets veranderen: dezelfde tag nog eens kiezen, dezelfde state
     * nog eens zetten, en de zichtbaarheid van de tag waarin gewerkt wordt uitzetten.
     */
    private luisterNaarHetCondtagpaneel(): void {
        this.condtagService.initialiseer({
            //het open- en dichtklappen van het paneel is sinds 09-09-2026 lokaal gedrag (wens
            //gebruiker in de testronde van a4): of jij je gereedschap open hebt staan zegt niets
            //over het model, en het beeld van de ander hoort er niet door te verspringen. Alles wat
            //de conditiesets zélf raakt — aanmaken, kiezen, hernoemen, zichtbaarheid, verwijderen —
            //blijft wel over de lijn gaan
            toggleOpen: open => this.condtagService.zetOpen(open),
            kiesTag: tag => {
                if (this.condtagService.condtag()?.tag !== tag) {
                    this.doe('setCondtag', {tag, state: 'cond'}); //altijd beginnen bij de conditie
                }
            },
            zetState: state => {
                const actief = this.condtagService.condtag();
                if (actief && actief.state !== state) {
                    this.doe('setCondtag', {tag: actief.tag, state});
                }
            },
            unsetTag: () => this.doe('setCondtag', {tag: null, state: null}),
            toggleView: tag => {
                if (this.condtagService.condtag()?.tag !== tag) {
                    this.doe('setViewtag', {tag, toon: !this.condtagService.conditioneleTags()[tag]});
                }
            },
            nieuweTag: naam => this.doe('changeCondtag', {oudeNaam: null, nieuweNaam: naam}),
            wijzigTag: (oud, nieuw) => this.doe('changeCondtag', {oudeNaam: oud, nieuweNaam: nieuw}),
            verwijderTag: tag => this.doe('changeCondtag', {oudeNaam: tag, nieuweNaam: null})
        });
    }

    /**
     * Een volger klikte een toggle: het verzoek gaat naar de leider. De simfeedback heeft zijn eigen
     * lijn — daar gaat de hele simulatiestatus overheen, niet één vlag.
     */
    private vraagToggle(soort: FeedbackToggleSoort, toon: boolean): void {
        switch (soort) {
            case 'sim':
                this.stuurSim('simfeedbackactie', {feedbackactie: 'toggle', feedbackarg: toon});
                break;
            case 'norm':
                this.stuur([{actie: 'normToon', toon}]);
                break;
            default:
                this.stuur([{actie: 'bsToon', soort, toon}]);
                break;
        }
    }

    /**
     * De staart na een uitgevoerde toggle: loggen, een undo-punt, de nieuwe stand naar de volgers en
     * opslaan. De balk heeft zijn vlag al gezet voordat hij dit meldt, dus het undo-punt staat hier
     * ná de mutatie — net als in AngularJS; zie de bevinding over de undo-punten van de
     * feedbacklagen in het testdocument van deze golf.
     * @param volger gevuld als het verzoek van een volger kwam, voor het actielog
     */
    private naToggle(soort: FeedbackToggleSoort, toon: boolean, volger?: string): void {
        if (soort === 'sim') {
            this.syncSimfeedback();
            this.opslag.directlogAction('simfb_show', {show: toon}, 'model', 'model', false, volger);
            this.opslag.markeerSnapshotNodig();
            this.opslag.setUndoPoint();
            this.startOpslagronde(true);
            return;
        }
        const actie = soort === 'norm'
            ? (toon ? 'norm_show' : 'norm_hide')
            : (toon ? `sug_show_${soort}` : `sug_hide_${soort}`);
        const bericht: Samenwerkactie = soort === 'norm'
            ? {actie: 'normToon', toon}
            : {actie: 'bsToon', soort, toon};
        this.voerUit({}, () => ({
            logregels: [this.opslag.legLogVast(actie, 'model', 'model', {}, volger, false)],
            bericht: [bericht],
            opslaan: 'relevant'
        }));
        this.opslag.setUndoPoint();
    }

    /**
     * Een selectie van een normfout of een bouwsuggestie bij leider/lokaal: naar de andere
     * deelnemers, de actielogregel, en de melding dat er een snapshot nodig is. Dat laatste sinds
     * besluit 15-09-2026: de drie aanwijzingen horen hetzelfde te doen, en de simfeedback-selectie
     * deed het al (`meldSimSelectie`).
     * @param bericht de samenwerkactie die de selectie beschrijft
     * @param logactie de naam van de actielogregel
     * @param payload wat er bij die regel hoort
     */
    private meldSelectie(bericht: Samenwerkactie, logactie: string, payload: object): void {
        this.stuur([bericht]);
        this.opslag.directlogAction(logactie, logargumenten(payload), 'model', 'model');
        this.opslag.markeerSnapshotNodig();
    }
}

/**
 * Brengt een getypeerde payload naar de vorm die de actielog verwacht. De logregel bewaart vrije
 * argumenten; de feedbacklagen leveren ze als eigen interface aan, en die heeft geen indexsignatuur.
 */
function logargumenten(payload: object): Record<string, unknown> {
    return {...payload} as Record<string, unknown>;
}
